import type {
  TicketAttachment,
  TicketDetailsData,
  UploadedTicketFile,
  UploadTicketFileResponse
} from './types';

import {Button, Loading, TextArea} from '@ds/components';
import {DetailPageLayout} from '@ds/layout';
import {toast} from '@Toast';
import AttachmentItem from 'common/ticketing-details/AttachmentItem';
import AttachmentUploader from 'common/ticketing-details/AttachmentUploader';
import TagDescriptionFrame from 'common/ticketing-details/TagDescriptionFrame/TagDescriptionFrame';
import TicketingInfo from 'common/ticketing-details/TicketingInfo';
import TicketingMessage from 'common/ticketing-details/TicketingMessage';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  addTicketComment,
  downloadAttachment,
  getTicket,
  uploadFiles
} from 'store/slices/ticket/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

type TicketLocationState = {
  orderCode?: string | null;
};

const readOrderCode = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const TicketDetails: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const {id: ticketId} = useParams<{id: string}>();
  const selectedVendor = getSelectedVendorFromStorage();
  const vendorId =
    typeof selectedVendor?.id === 'string' || typeof selectedVendor?.id === 'number'
      ? selectedVendor.id
      : '0';
  const ticket = useAppSelector((state) => state.ticket.ticket) as TicketDetailsData;
  const listedOrderCode = useAppSelector((state) => {
    const items = state.ticket.tickets?.tickets;
    if (!Array.isArray(items) || !ticketId) {
      return null;
    }
    const match = items.find(
      (item: {id?: string | number; order_code?: string | null}) =>
        String(item.id) === String(ticketId)
    );
    return readOrderCode(match?.order_code);
  });
  const loading = useAppSelector((state) => state.ticket.loading) as number;
  const navigationOrderCode = readOrderCode(
    (location.state as TicketLocationState | null)?.orderCode
  );
  const orderCode = readOrderCode(ticket?.order_code) ?? navigationOrderCode ?? listedOrderCode;
  const [files, setFiles] = useState<UploadedTicketFile[]>([]);
  const [description, setDescription] = useState('');

  const loadTicket = useCallback(() => {
    if (!ticketId) {
      return;
    }
    void dispatch(getTicket({ticket_id: ticketId, vendor_id: vendorId}));
  }, [dispatch, ticketId, vendorId]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  const handleUploadSuccess = (response: UploadTicketFileResponse) => {
    setFiles((currentFiles) => [...currentFiles, response.data]);
  };

  const handleRemoveFile = (file: TicketAttachment) => {
    if (!file.file_key) {
      return;
    }
    setFiles((currentFiles) =>
      currentFiles.filter((currentFile) => currentFile.file_key !== file.file_key)
    );
  };

  const addDescription = async () => {
    if (!ticketId) {
      return;
    }
    if (description.trim().length <= 10) {
      toast('توضیحات باید بیشتر از ۱۰ حرف باشد.');
      return;
    }

    await dispatch(
      addTicketComment({
        ticket_id: ticketId,
        vendor_id: vendorId,
        description: description.trim(),
        file_ids: files.length > 0 ? files.map(({file_key}) => file_key) : null
      })
    ).unwrap();

    loadTicket();
    toast('توضیحات با موفقیت ثبت شد');
    setDescription('');
    setFiles([]);
  };

  const comments = useMemo(
    () => (Array.isArray(ticket?.comments) ? ticket.comments : []),
    [ticket?.comments]
  );
  const ticketAttachments = Array.isArray(ticket?.attachments) ? ticket.attachments : [];
  const submitting = loading > 0;
  const isCurrentTicket = String(ticket?.id) === ticketId;

  if (submitting && !ticket?.id) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" color="primary" />
      </div>
    );
  }

  return (
    <DetailPageLayout
      footer={
        <Button
          disabled={description.trim().length <= 10}
          loading={submitting}
          onClick={addDescription}
          fullWidth
        >
          افزودن توضیحات
        </Button>
      }
    >
      <TicketingInfo data={{...(ticket ?? {}), order_code: orderCode}} />

      <div className="h-2 shrink-0 bg-gray-02" aria-hidden />

      <section className="flex flex-col gap-2.5 p-2.5">
        <h2 className="py-2.5 text-body-md-emphasis text-neutral-fg">توضیحات شما</h2>
        {isCurrentTicket && <TagDescriptionFrame html={ticket?.tag_description} />}
        <TicketingMessage
          description={ticket?.description}
          attachments={ticketAttachments}
          vendorId={vendorId}
          downloadAttachment={downloadAttachment}
        />
      </section>

      {comments.length > 0 ? (
        <section className="flex flex-col gap-2.5 p-2.5" aria-label="پاسخ‌های درخواست">
          {comments.map((comment) => (
            <TicketingMessage
              key={comment.id}
              title={comment.is_zf ? 'پاسخ اسنپ' : 'پاسخ شما'}
              description={comment.description}
              createdAt={comment.created_at}
              attachments={comment.attachments}
              vendorId={vendorId}
              downloadAttachment={downloadAttachment}
              source={comment.is_zf ? 'snapp' : 'vendor'}
            />
          ))}
        </section>
      ) : null}

      <div className="h-2 shrink-0 bg-gray-02" aria-hidden />

      <section className="flex flex-col gap-2.5 p-2.5">
        <h2 className="py-2.5 text-body-md-emphasis text-neutral-fg">
          نیاز به توضیح دارید؟ اینجا بنویسید
        </h2>
        <TextArea
          value={description}
          rows={6}
          placeholder="توضیح جزئیات مشکل"
          help="توضیحات باید بیشتر از ۱۰ حرف باشد."
          status={description.length > 0 && description.trim().length <= 10 ? 'error' : undefined}
          onChange={(event) => setDescription(event.target.value)}
        />
        <AttachmentUploader
          vendorId={vendorId}
          uploadAttachment={uploadFiles}
          onUploadSuccess={handleUploadSuccess}
        />

        {files.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            {files.map((file) => (
              <AttachmentItem
                key={file.file_key}
                file={file}
                vendorId={vendorId}
                downloadAttachment={downloadAttachment}
                removable
                onRemove={handleRemoveFile}
              />
            ))}
          </div>
        ) : null}
      </section>
    </DetailPageLayout>
  );
};

export default TicketDetails;
